import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { GripVertical } from "lucide-react";

/**
 * ColumnModals — Renders both Customize Columns and Rearrange Columns modals.
 * Used alongside useColumnManager hook.
 *
 * @param {object} columnManager - Return value from useColumnManager()
 * @param {Array} defaultColumns - Full column definitions array [{ id, label }]
 * @param {Array} customizableIds - Which column IDs can be hidden/shown
 */
export function ColumnModals({ columnManager, defaultColumns, customizableIds = [] }) {
  const {
    customizeOpen,
    rearrangeOpen,
    tempHidden,
    tempOrder,
    dragIdx,
    overIdx,
    setCustomizeOpen,
    setRearrangeOpen,
    saveCustomize,
    toggleTempCol,
    saveRearrange,
    onDragStart,
    onDragOverIdx,
    onDragEnd,
    onDrop,
  } = columnManager;

  return (
    <>
      {/* Customize Columns Modal */}
      <Dialog open={customizeOpen} onOpenChange={setCustomizeOpen}>
        <DialogContent className="sm:max-w-md" data-testid="customize-columns-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold uppercase tracking-tight">
              Customize Columns
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {customizableIds.map((id) => {
              const col = defaultColumns.find((c) => c.id === id);
              return (
                <label
                  key={id}
                  className="flex items-center gap-3 cursor-pointer"
                  data-testid={`toggle-col-${id}`}
                >
                  <input
                    type="checkbox"
                    checked={!tempHidden.includes(id)}
                    onChange={() => toggleTempCol(id)}
                    className="h-4 w-4 rounded border-gray-300 accent-black"
                  />
                  <span className="text-sm font-medium">{col?.label}</span>
                </label>
              );
            })}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={() => setCustomizeOpen(false)}
              data-testid="customize-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="rounded-sm bg-black text-white hover:bg-gray-800"
              onClick={saveCustomize}
              data-testid="customize-save-btn"
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rearrange Columns Modal */}
      <Dialog open={rearrangeOpen} onOpenChange={setRearrangeOpen}>
        <DialogContent className="sm:max-w-md" data-testid="rearrange-columns-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold uppercase tracking-tight">
              Rearrange Columns
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1 py-2 max-h-[400px] overflow-y-auto">
            {tempOrder.map((id, idx) => {
              const col = defaultColumns.find((c) => c.id === id);
              if (!col) return null;
              return (
                <div key={id}>
                {overIdx === idx && dragIdx !== null && dragIdx !== idx && (
                  <div className="h-0.5 bg-black rounded-full my-1" data-testid="drop-indicator" />
                )}
                <div
                  className={`flex items-center gap-3 p-2.5 rounded-sm border cursor-grab active:cursor-grabbing select-none ${
                    dragIdx === idx
                      ? "border-black bg-gray-100"
                      : "border-gray-200 bg-white"
                  }`}
                  draggable
                  onDragStart={() => onDragStart(idx)}
                  onDragOver={(e) => {
                    e.preventDefault();
                    onDragOverIdx(idx);
                    // Auto-scroll when dragging near the edges of the list.
                    const box = e.currentTarget.parentElement?.parentElement;
                    if (!box) return;
                    const r = box.getBoundingClientRect();
                    if (e.clientY - r.top < 40) box.scrollTop -= 12;
                    else if (r.bottom - e.clientY < 40) box.scrollTop += 12;
                  }}
                  onDrop={() => onDrop(idx)}
                  onDragEnd={onDragEnd}
                  data-testid={`drag-col-${id}`}
                >
                  <GripVertical
                    className="w-4 h-4 text-gray-400 shrink-0"
                    strokeWidth={1.5}
                  />
                  <span className="text-sm font-medium">
                    {col.label || "Actions"}
                  </span>
                </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={() => setRearrangeOpen(false)}
              data-testid="rearrange-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="rounded-sm bg-black text-white hover:bg-gray-800"
              onClick={saveRearrange}
              data-testid="rearrange-save-btn"
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
